import { type ReactNode } from 'react';

import { useTranslation } from 'react-i18next';
import { Loader2Icon } from 'lucide-react';

import { AIMessage, OculomicsSearchResponse } from '@/api/ai-search';
import { cn } from '@/utils/styles';

import { StructuredAIResponse } from './structured-ai-response';

// Helper to extract parsed response from ai_raw
function extractParsedResponse(message: AIMessage): OculomicsSearchResponse | null {
  if (!message.ai_raw?.output) return null;

  for (const output of message.ai_raw.output) {
    if (output.type === 'message' && output.content) {
      for (const content of output.content) {
        if (content.parsed) {
          return content.parsed;
        }
      }
    }
  }

  return null;
}

interface AIMessageBubbleProps {
  message: AIMessage;
  onOpenSource: (fileName: string) => void;
  previewFile: string | null;
  previewLoading: boolean;
}

export function AIMessageBubble({
  message,
  onOpenSource,
  previewFile,
  previewLoading,
}: AIMessageBubbleProps) {
  const { t } = useTranslation();

  const parsedData = extractParsedResponse(message);

  // Render message content but replace any [REF: "file-..." - {"document":"..."}] tokens with clickable links.
  const renderContentWithRefs = (text: string): ReactNode => {
    if (!text) return '';

    const regex = /\[REF:\s*"(?<id>[^"]+)"\s*-\s*(?<meta>\{.*?\})\s*\]/g;
    const nodes: ReactNode[] = [];
    let lastIndex = 0;

    for (const match of text.matchAll(regex)) {
      const m = match as RegExpMatchArray & { groups?: { id?: string; meta?: string } };
      const start = m.index ?? 0;
      const end = start + m[0].length;

      // Add text before the match
      if (start > lastIndex) nodes.push(text.slice(lastIndex, start));

      const fileId = m.groups?.id;
      let label: string | undefined;
      try {
        const metaUnknown: unknown = m.groups?.meta ? JSON.parse(m.groups.meta) : undefined;
        if (metaUnknown && typeof metaUnknown === 'object' && 'document' in metaUnknown) {
          const maybeDoc = (metaUnknown as { document?: unknown }).document;
          if (typeof maybeDoc === 'string') label = maybeDoc;
        }
      } catch {
        // ignore JSON parsing errors
      }

      // Insert clickable link for the REF
      if (label) {
        nodes.push(
          <button
            key={`${fileId}-${start}`}
            type="button"
            onClick={() => void onOpenSource(label)}
            className={cn(
              'underline text-primary hover:opacity-80 inline-flex items-center gap-1',
              previewLoading ? 'cursor-wait opacity-70' : 'cursor-pointer'
            )}
            disabled={previewLoading && previewFile === label}
          >
            <span>{label}</span>
            {previewLoading && previewFile === label ? (
              <Loader2Icon className="size-3 animate-spin" />
            ) : null}
          </button>
        );
      } else {
        // If no fileId parsed, keep original token text
        nodes.push(m[0]);
      }

      lastIndex = end;
    }

    // Add the remaining text
    if (lastIndex < text.length) nodes.push(text.slice(lastIndex));

    return <span className="whitespace-pre-wrap">{nodes}</span>;
  };

  // If we have structured data from ai_raw, render it nicely
  if (message.direction === 'outbound' && parsedData) {
    return (
      <div className="rounded-md border p-4 text-xs leading-relaxed bg-primary/10 border-primary/20 space-y-4">
        <div className="flex justify-between mb-2 opacity-60 text-[10px]">
          <span>AI Search Bot</span>
          <span>{new Date(message.created_at).toLocaleTimeString()}</span>
        </div>

        <StructuredAIResponse
          parsedData={parsedData}
          onOpenSource={onOpenSource}
          previewFile={previewFile}
          previewLoading={previewLoading}
        />
      </div>
    );
  }

  // Fallback to legacy rendering with content links
  const contentWithLinks = renderContentWithRefs(message.content);
  return (
    <div
      className={
        'rounded-md border p-2 text-xs leading-relaxed ' +
        (message.direction === 'inbound'
          ? 'bg-muted/60 border-transparent'
          : 'bg-primary/10 border-primary/20')
      }
    >
      <div className="flex justify-between mb-1 opacity-60 text-[10px]">
        <span>{message.direction === 'inbound' ? t('ai-search-label') : 'AI Search Bot'}</span>
        <span>{new Date(message.created_at).toLocaleTimeString()}</span>
      </div>
      <div className="mb-2">{contentWithLinks}</div>
    </div>
  );
}
